import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import MonthPicker from '@/components/MonthPicker';
import MainChargesSyncModal from './MainChargesSyncModal';
import MainChargesDeleteModal from './MainChargesDeleteModal';
import ExtrasChargesSyncModal from './ExtrasChargesSyncModal';
import TeacherChargesSyncModal from './TeacherChargesSyncModal';
import RecomputedChangesModal from './RecomputedChangesModal';

/**
 * Страница «Начисления». Три вкладки:
 *   • «Основной договор» — школа/сад. Расчёт за месяц с учётом
 *     производственного календаря (полная сумма / пропорция по рабочим дням).
 *   • «Доп. услуги» — помесячная агрегация начислений по доп. занятиям
 *     (по ученикам), выгрузка в 1С одним документом на ученика.
 *   • «Учителям» — плоский реестр начислений по учителям за период.
 */
const URL_SCHEMA = {
  tab:     'main',     // 'main' | 'extras' | 'teachers'
  // Учителям (плоский реестр)
  pending: '1',        // '' = все, '1' = ожидают, '0' = выгружены
  from:    '',
  to:      '',
  // Помесячные вкладки
  month:   '',         // YYYY-MM
};

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}
function fmtMoneyShort(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₽';
}
function fmtDate(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 16);
}
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

/**
 * Часы в «человеческом» формате. Сейчас часы АКАДЕМИЧЕСКИЕ (1 занятие = 1 час
 * в табеле ЗУП, согласовано с кадровиками), поэтому значения целые — «12 ч».
 * Дробные ветки оставлены для старых биллингов-снапшотов, выгруженных до
 * перехода (там часы считались из длительности слота, например 0.67):
 *   < 1 ч       → «40 мин»
 *   целое       → «3 ч»
 *   дробное     → «3 ч 30 мин»
 * Округление минут отбрасывает мелкие хвосты от плавающей точки.
 */
function fmtHours(v) {
  const n = Number(v) || 0;
  if (n <= 0) return '0 ч';
  const totalMin = Math.round(n * 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return m + ' мин';
  if (m === 0) return h + ' ч';
  return h + ' ч ' + m + ' мин';
}

export default function ExtraChargesPage() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);
  const tab = ['extras', 'teachers'].includes(params.tab) ? params.tab : 'main';

  return (
    <>
      <Topbar
        breadcrumb={['Финансы', 'Начисления']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="charges-tabs">
        <button
          className={'charges-tabs__btn' + (tab === 'main'   ? ' is-active' : '')}
          onClick={() => setParams({ tab: 'main' })}
        >
          Основной договор
        </button>
        <button
          className={'charges-tabs__btn' + (tab === 'extras' ? ' is-active' : '')}
          onClick={() => setParams({ tab: 'extras' })}
        >
          Доп. услуги
        </button>
        <button
          className={'charges-tabs__btn' + (tab === 'teachers' ? ' is-active' : '')}
          onClick={() => setParams({ tab: 'teachers' })}
        >
          Учителям
        </button>
      </div>

      {tab === 'main'     && <MainChargesTab     params={params} setParams={setParams} />}
      {tab === 'extras'   && <ExtrasMonthlyTab   params={params} setParams={setParams} />}
      {tab === 'teachers' && <TeachersChargesTab params={params} setParams={setParams} />}
    </>
  );
}

/* ============================================================================ */
/*  ВКЛАДКА: ОСНОВНОЙ ДОГОВОР                                                   */
/* ============================================================================ */

function MainChargesTab({ params, setParams }) {
  const can = useCan();
  const canWrite    = can('extra.charges',      'write');
  const canSendOnec = can('extra.charges.onec', 'write');

  // Дефолт «текущий месяц» при первом заходе
  useEffect(() => {
    if (!params.month) {
      const d = new Date();
      const m = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      setParams({ month: m });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError]  = useState(null);
  const [busy, setBusy]    = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Чекбоксы рядом с кнопками
  const [recompute, setRecompute] = useState(false);
  const [overwrite, setOverwrite] = useState(false);

  // Активный jobId выгрузки → открытая модалка прогресса
  const [syncJobId, setSyncJobId] = useState(null);

  // Результат пересчёта с найденными расхождениями → открытая модалка-сводка.
  // Показываем только если backend вернул changedCount > 0.
  const [recomputed, setRecomputed] = useState(null);

  // Фоновый job пакетного удаления → открытая модалка прогресса.
  // { jobId, total } после старта; null когда модалка не открыта.
  const [deleteJob, setDeleteJob] = useState(null);

  // Множественный выбор начислений для пакетных операций (удаление).
  // Чекбокс рисуется только у УЖЕ НАЧИСЛЕННЫХ строк — у «не начисленных»
  // удалять нечего (chargeId == null).
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    if (!params.month) return;
    setLoad(true); setError(null);
    api.get('/api/charges/main-monthly?month=' + encodeURIComponent(params.month))
      .then((res) => {
        setData(res);
        // При смене месяца / перезагрузке — сбросим выделение, т.к. id
        // могут уже не соответствовать актуальному списку.
        setSelectedIds(new Set());
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [params.month, reloadTick]);

  const reload = () => setReloadTick((n) => n + 1);

  // Все начисленные строки (то, что вообще можно удалить).
  const accruedItems = useMemo(
    () => (data?.items || []).filter((it) => it.isAccrued && it.chargeId),
    [data]
  );
  const allSelected = accruedItems.length > 0
    && accruedItems.every((it) => selectedIds.has(it.chargeId));

  const toggleOne = (chargeId, on) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(chargeId); else next.delete(chargeId);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) return new Set();
      const next = new Set(prev);
      for (const it of accruedItems) next.add(it.chargeId);
      return next;
    });
  };

  const onDeleteOne = async (chargeId, withOnec) => {
    if (!canWrite) return;
    const it = (data?.items || []).find((x) => x.chargeId === chargeId);
    const monthLabel = (data?.month || params.month || '').replace('-', '.');
    const confirmText = withOnec
      ? `Удалить начисление за ${monthLabel} (${it?.studentName || '#' + chargeId})?\n\n`
        + `Документ 1С «${it?.onecDocumentId}» тоже будет помечен на удаление`
        + (it?.onecInvoiceNumber ? ` (включая счёт-фактуру ${it.onecInvoiceNumber})` : '')
        + '. Если 1С недоступна — удаление прервётся.'
      : `Удалить начисление за ${monthLabel} (${it?.studentName || '#' + chargeId})?`;
    if (!window.confirm(confirmText)) return;

    setBusy(true); setError(null);
    try {
      // Если строка выгружена в 1С — идём по асинхронному пути (jobId + WS).
      // Это даёт прогресс и не упирается в nginx-таймауты, даже если 1С
      // задумалась. Для невыгруженных — обычный синхронный DELETE.
      if (withOnec) {
        const res = await api.post('/api/charges/main-monthly/bulk-delete', { ids: [chargeId] });
        if (res?.jobId) {
          setDeleteJob({ jobId: res.jobId, total: 1 });
        }
        setSelectedIds((prev) => {
          const next = new Set(prev); next.delete(chargeId); return next;
        });
        return;
      }

      await api.delete('/api/charges/main-monthly/' + chargeId);
      setSelectedIds((prev) => {
        const next = new Set(prev); next.delete(chargeId); return next;
      });
      reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    } finally {
      setBusy(false);
    }
  };

  const onDeleteSelected = async () => {
    if (!canWrite) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;

    // Сколько среди них уже выгружены в 1С.
    const uploadedCount = (data?.items || [])
      .filter((it) => selectedIds.has(it.chargeId) && it.onecUploadedAt)
      .length;

    const lines = [`Удалить выбранные начисления (${ids.length} шт.)?`];
    if (uploadedCount > 0) {
      lines.push('');
      lines.push(`${uploadedCount} из них уже выгружено в 1С — соответствующие документы реализации (и счета-фактуры) будут помечены на удаление через расширение 1С.`);
      lines.push('Если 1С недоступна, удалятся только те, что прошли в 1С; остальные останутся в CRM, их можно повторить.');
    }
    if (!window.confirm(lines.join('\n'))) return;

    setBusy(true); setError(null);
    try {
      // Бэкенд теперь возвращает jobId мгновенно — реальная работа идёт
      // в Node, прогресс приходит в WS как `sync.accrual-delete.event`.
      const res = await api.post('/api/charges/main-monthly/bulk-delete', { ids });
      if (res?.jobId) {
        setDeleteJob({ jobId: res.jobId, total: res.total || ids.length });
      }
      setSelectedIds(new Set());
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось запустить удаление');
    } finally {
      setBusy(false);
    }
  };

  const onAccrue = async () => {
    if (!params.month) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/charges/main-monthly/accrue', {
        month:     params.month,
        recompute: recompute,
      });

      // Если пересчёт нашёл реальные расхождения — открываем модалку-сводку.
      // Простого alert «готово» не зовём, чтобы оператор сразу видел список.
      if (recompute && (res.changedCount || 0) > 0) {
        setRecomputed(res);
      } else {
        const lines = [];
        if (res.created) lines.push(`создано ${res.created}`);
        if (res.updated) lines.push(`пересчитано ${res.updated}`);
        if (res.skipped) lines.push(`пропущено ${res.skipped}`);
        alert('Готово: ' + (lines.join(', ') || 'нечего начислять'));
      }

      setRecompute(false);
      reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось начислить');
    } finally {
      setBusy(false);
    }
  };

  const onUpload = async () => {
    if (!params.month) return;
    if (!confirm(overwrite
        ? 'Перезаписать ВСЕ начисления в 1С (включая уже выгруженные)? Это создаст в 1С новые документы.'
        : 'Выгрузить в 1С все ещё не выгруженные начисления?')) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/charges/main-monthly/upload-onec', {
        month:     params.month,
        overwrite: overwrite,
      });
      setOverwrite(false);
      // Открываем модалку прогресса; синк уже стартовал в Node, события полетят в WS
      setSyncJobId(res.jobId);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось запустить выгрузку');
    } finally {
      setBusy(false);
    }
  };

  const totalCount         = data?.totalCount         || 0;
  const accruedCount       = data?.accruedCount       || 0;
  const pendingAccrual     = data?.pendingAccrualCount || 0;
  const uploadedCount      = data?.uploadedCount      || 0;
  const pendingUpload      = data?.pendingUploadCount || 0;
  const totalAmt           = data?.totalAmount        || 0;

  // Тексты кнопок — динамические в зависимости от состояния.
  // Когда идёт операция — показываем «…», чтобы оператор видел, что
  // запрос в пути, и не нажимал кнопку повторно.
  const accrueLabel = busy
    ? (recompute ? 'Пересчитываем…' : 'Начисляем…')
    : pendingAccrual > 0
      ? `Начислить (${pendingAccrual})`
      : (recompute ? 'Пересчитать всех' : 'Начислить');

  const uploadLabel = busy
    ? 'Готовим выгрузку…'
    : pendingUpload > 0
      ? `Выгрузить в 1С (${pendingUpload})`
      : (overwrite ? 'Перевыгрузить всех' : 'Выгрузить в 1С');

  return (
    <>
      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>Месяц</label>
            <MonthPicker value={params.month || ''} onChange={(v) => setParams({ month: v })} />
          </div>

          {data && (
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', alignSelf: 'center' }}>
              Договоров: <b>{totalCount}</b>
              {' · '}рабочих дней: <b>{data.workingDaysInMonth}</b>
              {' · '}итого: <b>{fmtMoneyShort(totalAmt)}</b>
            </div>
          )}

          <div className="filterbar__spacer" />

          {/* Кнопка «Начислить» + чекбокс пересчёта */}
          {canWrite && (
            <div className="charges-action">
              {accruedCount > 0 && (
                <label className="charges-action__check" title="Пересчитать заново уже начисленные (не трогает выгруженные в 1С)">
                  <input type="checkbox" checked={recompute} onChange={(e) => setRecompute(e.target.checked)} />
                  Пересчитать
                </label>
              )}
              <button className="btn btn--primary btn--sm" disabled={busy || pendingAccrual === 0 && !recompute} onClick={onAccrue}>
                <Icon name="check" size={13} /> {accrueLabel}
              </button>
            </div>
          )}

          {/* Кнопка «Выгрузить в 1С» + чекбокс перезаписи */}
          {canSendOnec && (
            <div className="charges-action">
              {uploadedCount > 0 && (
                <label className="charges-action__check" title="Перезаписать в 1С даже уже выгруженные">
                  <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                  Перезаписать
                </label>
              )}
              <button
                className="btn btn--sm"
                disabled={busy || (pendingUpload === 0 && !overwrite) || accruedCount === 0}
                onClick={onUpload}
                title={accruedCount === 0 ? 'Сначала нажмите «Начислить»' : undefined}
              >
                <Icon name="upload" size={13} /> {uploadLabel}
              </button>
            </div>
          )}

          {/* Действия с выбранными строками — видна, только если есть начисленные */}
          {canWrite && accruedItems.length > 0 && (
            <div className="charges-action">
              <label
                className="charges-action__check"
                title="Выбрать/снять все начисленные строки"
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                />
                Все ({accruedItems.length})
              </label>
              <button
                className="btn btn--sm btn--danger"
                disabled={busy || selectedIds.size === 0}
                onClick={onDeleteSelected}
                title={selectedIds.size === 0 ? 'Выберите строки чекбоксами' : undefined}
              >
                <Icon name="x" size={13} /> Удалить {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="main-content-wrap" style={{ position: 'relative' }}>
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {!loading && data && data.items.length === 0 && (
          <div className="ref-empty" style={{ padding: 32 }}>
            На выбранный месяц нет активных договоров «школа / детский сад».
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="charges-list">
            {data.items.map((it) => (
              <MainChargeRow
                key={it.agreement.id}
                item={it}
                canWrite={canWrite}
                busy={busy}
                selected={it.chargeId ? selectedIds.has(it.chargeId) : false}
                onToggleSelect={(on) => it.chargeId && toggleOne(it.chargeId, on)}
                onDelete={() => it.chargeId && onDeleteOne(it.chargeId, !!it.onecUploadedAt)}
              />
            ))}
          </div>
        )}

        {/* Прозрачный «затвор» над списком, пока идёт операция начисления /
            выгрузки / удаления. Блокирует случайный клик по строкам и
            визуально подсвечивает «идёт работа». */}
        {busy && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.55)',
              backdropFilter: 'blur(1px)',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              paddingTop: 80,
              fontSize: 14,
              color: 'var(--fg-muted)',
              zIndex: 5,
              pointerEvents: 'all',
            }}
          >
            <div
              style={{
                padding: '10px 16px',
                background: 'var(--panel)',
                border: '1px solid var(--border-strong)',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span className="spinner" style={{
                width: 14, height: 14, border: '2px solid var(--border-strong)',
                borderTopColor: 'var(--fg)', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
              <span>
                {recompute ? 'Пересчитываем начисления…' : 'Идёт операция…'}
              </span>
            </div>
            <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>

      {syncJobId && (
        <MainChargesSyncModal
          jobId={syncJobId}
          month={params.month}
          onClose={() => { setSyncJobId(null); reload(); }}
        />
      )}

      {recomputed && (
        <RecomputedChangesModal
          summary={recomputed}
          month={params.month}
          onClose={() => { setRecomputed(null); reload(); }}
          onAfterPush={() => reload()}
        />
      )}

      {deleteJob && (
        <MainChargesDeleteModal
          jobId={deleteJob.jobId}
          total={deleteJob.total}
          onClose={() => { setDeleteJob(null); reload(); }}
          onDone={() => reload()}
        />
      )}
    </>
  );
}

function MainChargeRow({ item, canWrite, busy, selected, onToggleSelect, onDelete }) {
  const a = item.agreement;
  const t = item.tariff;
  const c = item.calc;
  const isAccrued  = !!item.isAccrued;
  const isUploaded = !!item.onecUploadedAt;

  const typeLabel = a.type === 'school' ? 'Школа' : 'Детский сад';

  return (
    <div className={'charge-row' + (isUploaded ? ' is-uploaded' : (isAccrued ? ' is-accrued' : ''))
                  + (selected ? ' is-selected' : '')}>
      <div className="charge-row__head">
        {/* Левая группа: чекбокс + основной блок. Группируем во flex,
            чтобы space-between на .charge-row__head не растягивал
            пробел между чекбоксом и ФИО. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, minWidth: 0, flex: 1 }}>
          {canWrite && isAccrued && item.chargeId ? (
            <input
              type="checkbox"
              className="charge-row__select"
              checked={!!selected}
              onChange={(e) => onToggleSelect?.(e.target.checked)}
              style={{ marginTop: 4, flex: '0 0 auto' }}
              title="Выбрать для пакетного удаления"
            />
          ) : null}
          <div className="charge-row__main">
          <div className="charge-row__name">{item.studentName}</div>
          <div className="charge-row__meta">
            <span className="agr-row__type-pill">{typeLabel}</span>
            <span>Договор № {a.number} от {fmtDateRu(a.date)}</span>
            {a.onecCode && <span className="muted mono">· 1С: {a.onecCode}</span>}
            {isUploaded
              ? <span className="pay-st pay-st--ok">Выгружено в 1С</span>
              : isAccrued
                ? <span className="pay-st pay-st--draft">Начислено</span>
                : <span className="pay-st pay-st--mute">Не начислено</span>}
          </div>
          {t.name && (
            <div className="charge-row__meta muted">
              Тариф: {t.name} · {fmtMoneyShort(t.price)}/мес
            </div>
          )}
          </div>
        </div>
        {/* Правая группа: сумма + кнопка удаления. Снова один flex,
            чтобы кнопка ехала прямо после суммы без зазора. */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div className="charge-row__amount">
            <div className="charge-row__amount-cap">{isAccrued ? 'Начислено' : 'К начислению'}</div>
            <div className="charge-row__amount-val mono">{fmtMoneyShort(c.amount)}</div>
          </div>
          {canWrite && isAccrued && item.chargeId && onDelete && (
            <button
              type="button"
              className="btn btn--sm btn--danger"
              disabled={busy}
              onClick={onDelete}
              title={isUploaded
                ? 'Удалить начисление (и пометить документ в 1С на удаление)'
                : 'Удалить начисление'}
              style={{ flex: '0 0 auto' }}
            >
              <Icon name="x" size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="charge-row__formula">
        {c.isFullMonth ? (
          <>
            <Icon name="check" size={11} />
            <span>Полный месяц по договору — {fmtMoneyShort(t.price)}</span>
          </>
        ) : (
          <>
            <span className="muted">
              Период в этом месяце: {fmtDateRu(c.rangeFrom)} — {fmtDateRu(c.rangeTo)}
            </span>
            <span className="charge-row__formula-calc mono">
              {fmtMoneyShort(t.price)} × {c.workingDaysInRange}/{c.workingDaysInMonth} раб.дн ={' '}
              <b>{fmtMoneyShort(c.amount)}</b>
            </span>
          </>
        )}
      </div>

      <div className="charge-row__onec">
        <div>
          <span className="muted">Документ реализации:</span>{' '}
          {item.onecDocumentId
            ? <span className="mono">{item.onecDocumentId}</span>
            : <span className="muted">— ещё не выгружено</span>}
        </div>
        <div>
          <span className="muted">Счёт-фактура:</span>{' '}
          {item.onecInvoiceNumber
            ? <span className="mono">{item.onecInvoiceNumber}</span>
            : <span className="muted">—</span>}
        </div>
      </div>
    </div>
  );
}

/* ============================================================================ */
/*  ВКЛАДКА: ДОП. УСЛУГИ — ПОМЕСЯЧНАЯ АГРЕГАЦИЯ ПО УЧЕНИКАМ                    */
/* ============================================================================ */

function ExtrasMonthlyTab({ params, setParams }) {
  const can = useCan();
  const canSendOnec = can('extra.charges.onec', 'write');

  // Дефолт «текущий месяц» при первом заходе.
  useEffect(() => {
    if (!params.month) {
      const d = new Date();
      const m = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      setParams({ month: m });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData]    = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError]  = useState(null);
  const [busy, setBusy]    = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  const [overwrite, setOverwrite] = useState(false);
  const [syncJobId, setSyncJobId] = useState(null);

  useEffect(() => {
    if (!params.month) return;
    setLoad(true); setError(null);
    api.get('/api/charges/extras-monthly?month=' + encodeURIComponent(params.month))
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [params.month, reloadTick]);

  const reload = () => setReloadTick((n) => n + 1);

  const onUpload = async () => {
    if (!params.month) return;
    if (!confirm(overwrite
        ? 'Перезаписать ВСЕ начисления в 1С (включая уже выгруженные)? Это создаст в 1С новые документы.'
        : 'Выгрузить в 1С все ещё не выгруженные начисления?')) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/charges/extras-monthly/upload-onec', {
        month:     params.month,
        overwrite: overwrite,
      });
      setOverwrite(false);
      setSyncJobId(res.jobId);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось запустить выгрузку');
    } finally {
      setBusy(false);
    }
  };

  const totalCount    = data?.totalCount         || 0;
  const uploadedCount = data?.uploadedCount      || 0;
  const pendingUpload = data?.pendingUploadCount || 0;
  const noAgreement   = data?.noAgreementCount   || 0;
  const noOnec        = data?.noOnecCount        || 0;
  const totalAmt      = data?.totalAmount        || 0;

  const uploadLabel = pendingUpload > 0
    ? `Выгрузить в 1С (${pendingUpload})`
    : (overwrite ? 'Перевыгрузить всех' : 'Выгрузить в 1С');

  return (
    <>
      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>Месяц</label>
            <MonthPicker value={params.month || ''} onChange={(v) => setParams({ month: v })} />
          </div>

          {data && (
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', alignSelf: 'center' }}>
              Учеников: <b>{totalCount}</b>
              {' · '}итого: <b>{fmtMoneyShort(totalAmt)}</b>
              {noAgreement > 0 && (
                <> {' · '}<span style={{ color: 'var(--red-fg)' }}>без договора: <b>{noAgreement}</b></span></>
              )}
              {noOnec > 0 && (
                <> {' · '}без 1С-связи: <b>{noOnec}</b></>
              )}
            </div>
          )}

          <div className="filterbar__spacer" />

          {canSendOnec && (
            <div className="charges-action">
              {uploadedCount > 0 && (
                <label className="charges-action__check" title="Перезаписать в 1С даже уже выгруженные">
                  <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                  Перезаписать
                </label>
              )}
              <button
                className="btn btn--primary btn--sm"
                disabled={busy || (pendingUpload === 0 && !overwrite) || totalCount === 0}
                onClick={onUpload}
                title={totalCount === 0 ? 'За этот месяц нет проведённых занятий' : undefined}
              >
                <Icon name="upload" size={13} /> {uploadLabel}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {!loading && data && data.items.length === 0 && (
          <div className="ref-empty" style={{ padding: 32 }}>
            На выбранный месяц нет проведённых доп. занятий.
            <div style={{ fontSize: 12, marginTop: 6, color: 'var(--fg-muted)' }}>
              Видны занятия со статусом «conducted». Запланированные не учитываются.
            </div>
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="charges-list">
            {data.items.map((it) => (
              <ExtrasMonthlyRow
                key={it.studentId + ':' + it.agreement.id}
                item={it}
                canSendOnec={canSendOnec}
                busy={busy}
                onUploadOne={async () => {
                  const uploaded = !!(it.billing && it.billing.onecUploadedAt);
                  if (!confirm(uploaded
                      ? `Перевыгрузить начисления «${it.studentName}» за ${params.month} в 1С? Документ будет пересобран с текущими суммами, остальные ученики не затрагиваются.`
                      : `Выгрузить начисления «${it.studentName}» за ${params.month} в 1С?`)) return;
                  setBusy(true);
                  try {
                    const res = await api.post('/api/charges/extras-monthly/upload-onec', {
                      month:       params.month,
                      overwrite:   uploaded,
                      studentId:   it.studentId,
                      agreementId: it.agreement?.id,
                    });
                    setSyncJobId(res.jobId);
                  } catch (e) {
                    alert(e instanceof ApiError ? e.message : 'Не удалось запустить выгрузку');
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {syncJobId && (
        <ExtrasChargesSyncModal
          jobId={syncJobId}
          month={params.month}
          onClose={() => { setSyncJobId(null); reload(); }}
        />
      )}
    </>
  );
}

function ExtrasMonthlyRow({ item, canSendOnec, busy, onUploadOne }) {
  const a = item.agreement;
  const b = item.billing;
  const noAgreement = !a || !a.id;
  const isUploaded  = !!(b && b.onecUploadedAt);
  const noOnec      = !item.onecContractorCode || !a.onecCode;

  return (
    <div className={'charge-row' + (isUploaded ? ' is-uploaded' : '')}>
      <div className="charge-row__head">
        <div className="charge-row__main">
          <div className="charge-row__name">{item.studentName}</div>
          <div className="charge-row__meta">
            {noAgreement
              ? <span className="muted">Без договора</span>
              : <>
                  <span>Договор № {a.number} от {fmtDateRu(a.date)}</span>
                  {a.onecCode && <span className="muted mono">· 1С: {a.onecCode}</span>}
                </>}
            {isUploaded
              ? <span className="pay-st pay-st--ok">Выгружено в 1С</span>
              : noAgreement
                ? <span className="pay-st pay-st--mute">Нет договора — выгрузить нельзя</span>
                : noOnec
                  ? <span className="pay-st pay-st--mute">Нет связи с 1С</span>
                  : <span className="pay-st pay-st--pending">Ожидает выгрузки</span>}
          </div>
        </div>
        <div className="charge-row__amount">
          <div className="charge-row__amount-cap">К начислению</div>
          <div className="charge-row__amount-val mono">{fmtMoneyShort(item.totalAmount)}</div>
        </div>
      </div>

      {/* Список «sales-линий» — что именно полетит в 1С (предмет × количество × цена). */}
      <ul className="charge-row__lessons">
        {item.lessons.map((l, i) => (
          <li key={i}>
            <span>{l.subjectName}</span>
            <span className="muted mono" style={{ fontSize: 11 }}>
              {l.count} × {fmtMoneyShort(l.unitPrice)}
              {l.onecSubjectCode ? '' : ' · нет 1С-кода'}
            </span>
            <span className="mono">{fmtMoneyShort(l.totalAmount)}</span>
          </li>
        ))}
      </ul>

      <div className="charge-row__onec">
        <div>
          <span className="muted">Документ реализации:</span>{' '}
          {b && b.onecDocumentId
            ? <span className="mono">{b.onecDocumentId}</span>
            : <span className="muted">— ещё не выгружено</span>}
        </div>
        <div>
          <span className="muted">Счёт-фактура:</span>{' '}
          {b && b.onecInvoiceNumber
            ? <span className="mono">{b.onecInvoiceNumber}</span>
            : <span className="muted">—</span>}
        </div>
        {canSendOnec && !noAgreement && !noOnec && onUploadOne && (
          <button
            type="button"
            className="btn btn--sm"
            disabled={busy}
            onClick={onUploadOne}
            title={isUploaded
              ? 'Пересобрать документ 1С этого ученика с текущими суммами (остальных не трогает)'
              : 'Выгрузить в 1С только этого ученика'}
            style={{ marginLeft: 'auto', alignSelf: 'center', flex: '0 0 auto' }}
          >
            <Icon name="upload" size={12} /> {isUploaded ? 'Перевыгрузить' : 'Выгрузить'}
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================================================================ */
/*  ВКЛАДКА: УЧИТЕЛЯМ — ПОМЕСЯЧНАЯ ВЫГРУЗКА В 1С ЗУП                           */
/* ============================================================================ */

function TeachersChargesTab({ params, setParams }) {
  const can = useCan();
  const canWrite    = can('extra.charges',      'write');
  const canSendOnec = can('extra.charges.onec', 'write');

  useEffect(() => {
    if (!params.month) {
      const d = new Date();
      const m = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      setParams({ month: m });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [data, setData] = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [recompute, setRecompute] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [syncJobId, setSyncJobId] = useState(null);

  useEffect(() => {
    if (!params.month) return;
    setLoad(true); setError(null);
    api.get('/api/charges/teachers-monthly?month=' + encodeURIComponent(params.month))
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [params.month, reloadTick]);

  const reload = () => setReloadTick((n) => n + 1);

  const onAccrue = async () => {
    if (!params.month) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/charges/teachers-monthly/accrue', {
        month:     params.month,
        recompute: recompute,
      });
      const lines = [];
      if (res.created) lines.push(`создано ${res.created}`);
      if (res.updated) lines.push(`пересчитано ${res.updated}`);
      if (res.skipped) lines.push(`пропущено ${res.skipped}`);
      alert('Готово: ' + (lines.join(', ') || 'нечего начислять'));
      setRecompute(false);
      reload();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось начислить');
    } finally { setBusy(false); }
  };

  const onUpload = async () => {
    if (!params.month) return;
    if (!confirm(overwrite
        ? 'Перезаписать ВСЕ зарплаты в 1С ЗУП? Документы «Разовое начисление» по предметам создадутся заново.'
        : 'Выгрузить в 1С ЗУП все ещё не выгруженные зарплаты учителей?')) return;
    setBusy(true); setError(null);
    try {
      const res = await api.post('/api/charges/teachers-monthly/upload-onec', {
        month:     params.month,
        overwrite: overwrite,
      });
      setOverwrite(false);
      setSyncJobId(res.jobId);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось запустить выгрузку');
    } finally { setBusy(false); }
  };

  const totalCount    = data?.totalCount         || 0;
  const accruedCount  = data?.accruedCount       || 0;
  const pendingAccrual = data?.pendingAccrualCount || 0;
  const uploadedCount = data?.uploadedCount      || 0;
  const pendingUpload = data?.pendingUploadCount || 0;
  const noEmployee    = data?.noEmployeeCount    || 0;
  const totalNet      = data?.totalAmountNet     || 0;
  const totalBrutto   = data?.totalAmountBrutto  || 0;
  const taxRate       = data?.taxRate            || 0.13;
  const zupConfigured = !!data?.zupConfigured;

  const accrueLabel = pendingAccrual > 0
    ? `Начислить (${pendingAccrual})`
    : (recompute ? 'Пересчитать всех' : 'Начислить');

  const uploadLabel = pendingUpload > 0
    ? `Выгрузить в 1С ЗУП (${pendingUpload})`
    : (overwrite ? 'Перевыгрузить всех' : 'Выгрузить в 1С ЗУП');

  return (
    <>
      <div className="filterbar">
        <div className="filterbar__row" style={{ alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <label style={{ fontSize: 11, color: 'var(--fg-muted)' }}>Месяц</label>
            <MonthPicker value={params.month || ''} onChange={(v) => setParams({ month: v })} />
          </div>

          {data && (
            <div style={{ fontSize: 12, color: 'var(--fg-muted)', alignSelf: 'center' }}>
              Учителей: <b>{totalCount}</b>
              {' · '}на руки: <b>{fmtMoneyShort(totalNet)}</b>
              {' · '}брутто (с НДФЛ {Math.round(taxRate * 100)}%): <b>{fmtMoneyShort(totalBrutto)}</b>
              {zupConfigured && noEmployee > 0 && (
                <> {' · '}<span style={{ color: 'var(--red-fg)' }}>без сотрудника ЗУП: <b>{noEmployee}</b></span></>
              )}
            </div>
          )}

          <div className="filterbar__spacer" />

          {/* «Начислить» — фиксирует snapshot в teacher_monthly_billings, дальше
              пересчёт только при чекбоксе. Симметрично основному договору. */}
          {canWrite && (
            <div className="charges-action">
              {accruedCount > 0 && (
                <label className="charges-action__check" title="Пересчитать заново уже начисленные (не трогает выгруженные в ЗУП)">
                  <input type="checkbox" checked={recompute} onChange={(e) => setRecompute(e.target.checked)} />
                  Пересчитать
                </label>
              )}
              <button
                className="btn btn--primary btn--sm"
                disabled={busy || (pendingAccrual === 0 && !recompute) || totalCount === 0}
                onClick={onAccrue}
              >
                <Icon name="check" size={13} /> {accrueLabel}
              </button>
            </div>
          )}

          {/* «Выгрузить в ЗУП» — показываем только когда настройки 1С ЗУП заданы.
              Пока API на стороне ЗУП не готов — base_url пустой, кнопка скрыта. */}
          {canSendOnec && zupConfigured && (
            <div className="charges-action">
              {uploadedCount > 0 && (
                <label className="charges-action__check" title="Перезаписать в 1С ЗУП даже уже выгруженные">
                  <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
                  Перезаписать
                </label>
              )}
              <button
                className="btn btn--sm"
                disabled={busy || (pendingUpload === 0 && !overwrite) || accruedCount === 0}
                onClick={onUpload}
                title={accruedCount === 0 ? 'Сначала нажмите «Начислить»' : undefined}
              >
                <Icon name="upload" size={13} /> {uploadLabel}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}
        {!loading && data && data.items.length === 0 && (
          <div className="ref-empty" style={{ padding: 32 }}>
            На выбранный месяц нет проведённых доп. занятий с учителем.
          </div>
        )}

        {data && data.items.length > 0 && (
          <div className="charges-list">
            {data.items.map((it) => (
              <TeacherMonthlyRow
                key={it.teacherId}
                item={it}
                taxRate={taxRate}
                zupConfigured={zupConfigured}
              />
            ))}
          </div>
        )}
      </div>

      {syncJobId && (
        <TeacherChargesSyncModal
          jobId={syncJobId}
          month={params.month}
          onClose={() => { setSyncJobId(null); reload(); }}
        />
      )}
    </>
  );
}

function TeacherMonthlyRow({ item, taxRate, zupConfigured }) {
  const b = item.billing;
  const isAccrued  = !!item.isAccrued;
  const isUploaded = !!(b && b.onecUploadedAt);
  const noEmployee = !item.onecEmployee;
  // Должность подработки больше не обязательна: схема «Разовые начисления
  // по предметам» платит напрямую сотруднику, вид начисления ЗУП создаёт сам.

  // Плашка статуса. ЗУП-сценарии (выгружено / нужен сотрудник)
  // показываем ТОЛЬКО когда ЗУП настроен. Иначе — просто «Начислено» / «Не начислено».
  let statusPill;
  if (isUploaded) {
    statusPill = <span className="pay-st pay-st--ok">Выгружено в ЗУП</span>;
  } else if (zupConfigured && noEmployee) {
    statusPill = <span className="pay-st pay-st--mute">Нужно привязать сотрудника</span>;
  } else if (isAccrued) {
    statusPill = <span className="pay-st pay-st--draft">Начислено</span>;
  } else {
    statusPill = <span className="pay-st pay-st--mute">Не начислено</span>;
  }

  return (
    <div className={'charge-row' + (isUploaded ? ' is-uploaded' : (isAccrued ? ' is-accrued' : ''))}>
      <div className="charge-row__head">
        <div className="charge-row__main">
          <div className="charge-row__name">{item.teacherName}</div>
          <div className="charge-row__meta">
            {item.onecEmployee
              ? <span className="muted mono">1С ЗУП: {item.onecEmployee.code}</span>
              : zupConfigured
                ? <span className="muted">Без привязки к 1С ЗУП</span>
                : null}
            {statusPill}
            {item.recomputedCount > 0 && (
              <span className="muted" style={{ fontSize: 11 }}>· пересчитано {item.recomputedCount}×</span>
            )}
          </div>
        </div>
        <div className="charge-row__amount">
          <div className="charge-row__amount-cap">
            На руки · {Math.round(taxRate * 100)}% НДФЛ ⇒ Брутто
          </div>
          <div className="charge-row__amount-val mono">
            {fmtMoneyShort(item.totalAmountNet)} → {fmtMoneyShort(item.totalAmountBrutto)}
          </div>
          <div
            className="muted mono"
            style={{ fontSize: 11 }}
            title={item.totalHours + ' ч (десятичные)'}
          >
            {fmtHours(item.totalHours)}
          </div>
        </div>
      </div>

      <ul className="charge-row__lessons">
        {item.assignments.map((a) => {
          // Старые снапшоты node-выгрузки не содержали lessonsCount и брутто —
          // добираем из «на руки» и ставки, чтобы не показывать нули.
          const brutto = a.totalAmountBrutto != null
            ? a.totalAmountBrutto
            : (taxRate < 1 ? (a.totalAmountNet || 0) / (1 - taxRate) : (a.totalAmountNet || 0));
          return (
            <li key={a.subjectId}>
              <span>{a.subjectName}</span>
              <span
                className="muted mono"
                style={{ fontSize: 11 }}
                title={a.totalHours + ' ч (десятичные)'}
              >
                {a.lessonsCount != null ? `${a.lessonsCount} зан. · ` : ''}
                {fmtHours(a.totalHours)} × {fmtMoneyShort(a.ratePerHour)}/ч
              </span>
              <span className="mono">{fmtMoneyShort(brutto)}</span>
            </li>
          );
        })}
      </ul>

      {/* Блок 1С ЗУП — показываем только когда подключение настроено. Без него
          пустые «— ещё не выгружено» только мешают. */}
      {zupConfigured && (
        <div className="charge-row__onec">
          <div>
            <span className="muted">Разовые начисления:</span>{' '}
            {b && b.onecAssignmentDocs && b.onecAssignmentDocs.length > 0
              ? <span className="mono">
                  {b.onecAssignmentDocs.map((d) => d.docNumber || d.doc_number).filter(Boolean).join(', ')}
                </span>
              : <span className="muted">— ещё не выгружено</span>}
          </div>
          {b && b.onecTimesheetDoc ? (
            <div>
              <span className="muted">Табель:</span>{' '}
              <span className="mono">{b.onecTimesheetDoc}</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/* ============================================================================ */
/*  УСТАРЕВШЕЕ: плоский реестр teacher_charges (был раньше во вкладке «Учителям»).
    Оставлен на случай, если потребуется отдельный «лог» начислений по часам.
    Сейчас не используется в навигации.                                        */
/* ============================================================================ */

function TeachersChargesTabFlat({ params, setParams }) {
  const can = useCan();
  const canMarkOnec = can('extra.charges.onec', 'write');

  const [data, setData] = useState({ rows: [], totalAmount: 0, pendingCount: 0, pendingAmount: 0 });
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = () => setReloadTick((n) => n + 1);

  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPicked(new Set()); }, [params.pending, params.from, params.to, reloadTick]);

  useEffect(() => {
    setLoad(true); setError(null);
    const qs = new URLSearchParams();
    qs.set('kind', 'teacher');
    if (params.pending !== '') qs.set('pending_onec', params.pending);
    if (params.from) qs.set('from', params.from);
    if (params.to)   qs.set('to',   params.to);
    api.get('/api/extras-charges?' + qs.toString())
      .then((res) => setData(res))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  }, [params.pending, params.from, params.to, reloadTick]);

  const togglePick = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };
  const toggleAllPending = () => {
    const ids = data.rows.filter((r) => !r.onecUploadedAt).map((r) => r.id);
    if (picked.size === ids.length) setPicked(new Set());
    else setPicked(new Set(ids));
  };

  const onMarkUploaded = async () => {
    if (picked.size === 0) return;
    if (!confirm(`Пометить ${picked.size} начислени(й/я) как выгруженные в 1С? После этого их нельзя будет удалить — только сторнировать.`)) return;
    setBusy(true);
    try {
      const res = await api.post('/api/extras-charges/mark-uploaded', {
        kind: 'teacher',
        ids:  Array.from(picked),
      });
      reload();
      alert(`Помечено: ${res.updated || 0}`);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось пометить');
    } finally {
      setBusy(false);
    }
  };

  const totalSelectedAmount = useMemo(() => {
    if (picked.size === 0) return 0;
    let s = 0;
    for (const r of data.rows) if (picked.has(r.id)) s += r.amount;
    return s;
  }, [picked, data.rows]);

  const pendingIds = useMemo(
    () => data.rows.filter((r) => !r.onecUploadedAt).map((r) => r.id),
    [data.rows]
  );

  return (
    <>
      <div className="filterbar">
        <div className="filterbar__row">
          <select
            value={params.pending}
            onChange={(e) => setParams({ pending: e.target.value })}
            style={{ height: 30 }}
          >
            <option value="1">Ожидают выгрузки в 1С</option>
            <option value="0">Уже выгруженные</option>
            <option value="">Все</option>
          </select>

          <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>с</label>
          <input type="date" value={params.from || ''}
                 onChange={(e) => setParams({ from: e.target.value })} className="chip-date" />
          <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>по</label>
          <input type="date" value={params.to || ''}
                 onChange={(e) => setParams({ to: e.target.value })} className="chip-date" />

          <div className="filterbar__spacer" />
          <span className="filterbar__meta">
            Всего: <b>{fmtMoney(data.totalAmount)}</b>
            {' · '}ожидают: <b>{data.pendingCount}</b> ({fmtMoney(data.pendingAmount)})
          </span>
          <div className="filterbar__rightActions">
            {canMarkOnec && params.pending !== '0' && (
              <button
                className="btn btn--primary btn--sm"
                disabled={busy || picked.size === 0}
                onClick={onMarkUploaded}
                title={picked.size === 0 ? 'Сначала отметьте строки' : undefined}
              >
                <Icon name="check" size={13} />
                Пометить выгруженными ({picked.size})
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="main-content-wrap">
        {error && <div style={{ padding: 16, color: 'var(--red-fg)' }}>Ошибка: {error}</div>}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                {canMarkOnec && (
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={pendingIds.length > 0 && picked.size === pendingIds.length}
                      onChange={toggleAllPending}
                      title="Выбрать все «ожидающие»"
                      disabled={pendingIds.length === 0}
                    />
                  </th>
                )}
                <th style={{ width: 110 }}>Дата занятия</th>
                <th>Учитель</th>
                <th>Описание</th>
                <th style={{ width: 130, textAlign: 'right' }}>Сумма</th>
                <th style={{ width: 130 }}>1С</th>
              </tr>
            </thead>
            <tbody>
              {loading && data.rows.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>Загрузка…</td></tr>
              )}
              {!loading && data.rows.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--fg-muted)' }}>Начислений не найдено.</td></tr>
              )}
              {data.rows.map((r) => {
                const pending = !r.onecUploadedAt;
                const isStorno = r.kind === 'extra_lesson_storno';
                return (
                  <tr key={r.id}>
                    {canMarkOnec && (
                      <td>
                        {pending ? (
                          <input type="checkbox" checked={picked.has(r.id)} onChange={() => togglePick(r.id)} />
                        ) : <span className="muted">—</span>}
                      </td>
                    )}
                    <td className="mono">{r.lessonDate || '—'}</td>
                    <td>{r.teacherName}</td>
                    <td className="muted" style={{ fontSize: 12 }}>
                      {r.description}
                      {isStorno && <span className="pay-st pay-st--mute" style={{ marginLeft: 6 }}>сторно</span>}
                    </td>
                    <td className="mono" style={{ textAlign: 'right',
                      color: r.amount < 0 ? 'var(--red-fg)' : undefined }}>
                      {fmtMoney(r.amount)}
                    </td>
                    <td>
                      {pending
                        ? <span className="pay-st pay-st--pending">ожидает</span>
                        : <span className="pay-st pay-st--ok" title={fmtDate(r.onecUploadedAt)}>выгружено</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {picked.size > 0 && (
          <div className="ec-selection-bar">
            <span>Выбрано: <b>{picked.size}</b> на сумму <b>{fmtMoney(totalSelectedAmount)}</b></span>
            <button className="btn btn--sm" onClick={() => setPicked(new Set())}>Снять выделение</button>
          </div>
        )}
      </div>
    </>
  );
}
